import React, { useState } from 'react';
import { Eye, EyeOff, Loader, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../lib/firebase';

interface LoginFormProps {
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const { signIn, signUp, authLoading } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetError, setResetError] = useState('');

  // Authorized domains for display
  const authorizedDomains = ['@lemon.no', '@eve.tech', '@oxivisuals.no', '@marketer.tech'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email || !formData.password) {
      setError('Email and password are required');
      return;
    }

    if (isRegistering) {
      // Registration validation
      if (!formData.displayName.trim()) {
        setError('Display name is required');
        return;
      }

      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      // Check if email matches authorized domains
      const emailLower = formData.email.toLowerCase();
      const isAuthorized = authorizedDomains.some(domain => emailLower.endsWith(domain));
      
      if (!isAuthorized) {
        setError(
          `Registration is restricted to authorized email domains: ${authorizedDomains.join(', ')}. ` +
          'Please contact the administrator if you believe your domain should be authorized.'
        );
        return;
      }

      try {
        await signUp({
          email: formData.email,
          password: formData.password,
          displayName: formData.displayName.trim()
        });
        
        setSuccess('Account created successfully! Welcome to Gule Sider.');
        
        // Clear form
        setFormData({
          email: '',
          password: '',
          displayName: '',
          confirmPassword: ''
        });
        
        onSuccess?.();
      } catch (err: any) {
        setError(err.message || 'Registration failed');
      }
    } else {
      // Sign in
      try {
        await signIn(formData.email, formData.password);
        onSuccess?.();
      } catch (err: any) {
        setError(err.message || 'Sign in failed');
      }
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);

    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    setResetLoading(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSuccess(true);
      setResetEmail('');
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        setResetError('No account found with this email address');
      } else if (err.code === 'auth/invalid-email') {
        setResetError('Please enter a valid email address');
      } else {
        setResetError(err.message || 'Failed to send reset email');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setError('');
    setSuccess('');
    setFormData({
      email: '',
      password: '',
      displayName: '',
      confirmPassword: ''
    });
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
              {isRegistering ? 'Create your account' : 'Sign in to your account'}
            </h2>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Gule Sider
            </p>
          </div>

          {/* Authorized domains info for registration */}
          {isRegistering && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Authorized Email Domains</h3>
                  <div className="mt-1 text-sm text-blue-700">
                    <p>Registration is available for users with email addresses from:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      {authorizedDomains.map(domain => (
                        <li key={domain} className="font-mono">{domain}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-green-700">{success}</div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {/* Display Name (Registration only) */}
              {isRegistering && (
                <div>
                  <label htmlFor="displayName" className="sr-only">
                    Full Name
                  </label>
                  <input
                    id="displayName"
                    name="displayName"
                    type="text"
                    required={isRegistering}
                    value={formData.displayName}
                    onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="relative block w-full px-3 py-2 border border-border placeholder-muted-foreground text-foreground bg-background rounded-lg focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Full Name"
                  />
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="relative block w-full px-3 py-2 border border-border placeholder-muted-foreground text-foreground bg-background rounded-lg focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Email address"
                />
              </div>

              {/* Password */}
              <div className="relative">
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isRegistering ? 'new-password' : 'current-password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="relative block w-full px-3 py-2 pr-10 border border-border placeholder-muted-foreground text-foreground bg-background rounded-lg focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <Eye className="h-5 w-5 text-muted-foreground" />
                  )}
                </button>
              </div>

              {/* Confirm Password (Registration only) */}
              {isRegistering && (
                <div>
                  <label htmlFor="confirmPassword" className="sr-only">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required={isRegistering}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="relative block w-full px-3 py-2 border border-border placeholder-muted-foreground text-foreground bg-background rounded-lg focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Confirm Password"
                  />
                </div>
              )}
            </div>

            {/* Forgot Password Link (Sign in only) */}
            {!isRegistering && (
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-primary hover:text-primary/80 underline transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={authLoading}
                className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 font-medium transition-colors"
              >
                {authLoading ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    {isRegistering ? 'Creating account...' : 'Signing in...'}
                  </>
                ) : (
                  isRegistering ? 'Create Account' : 'Sign In'
                )}
              </button>
            </div>

            {/* Toggle Mode */}
            <div className="text-center">
              <button
                type="button"
                onClick={toggleMode}
                className="text-sm text-foreground hover:text-primary underline transition-colors"
              >
                {isRegistering 
                  ? 'Already have an account? Sign in' 
                  : 'Need an account? Register here'
                }
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-foreground">Reset Password</h2>
                <button
                  onClick={closeForgotPasswordModal}
                  disabled={resetLoading}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X size={24} />
                </button>
              </div>

              {resetSuccess ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
                      <div className="text-sm text-green-700">
                        <p className="font-medium mb-1">Password reset email sent!</p>
                        <p>Check your inbox for instructions to reset your password.</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={closeForgotPasswordModal}
                    className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>

                  {resetError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="text-sm text-red-700">{resetError}</div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="resetEmail" className="block text-sm font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={closeForgotPasswordModal}
                      disabled={resetLoading}
                      className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-accent disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    >
                      {resetLoading ? (
                        <>
                          <Loader className="animate-spin" size={16} />
                          Sending...
                        </>
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};